import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { getAnalytics, logEvent, setUserProperties } from 'firebase/analytics';
import { getPerformance, trace } from 'firebase/performance';

const AnalyticsContainer = styled.div`
  max-width: 100%;
`;

const TestResults = styled.div`
  background: #f8f9fa;
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  border: 1px solid #eee;
`;

const TestGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
  margin: 1rem 0;
`;

const TestCard = styled.div`
  background: white;
  padding: 1rem;
  border-radius: 4px;
  box-shadow: 0 1px 2px rgba(0,0,0,0.05);

  h4 {
    margin: 0 0 0.5rem 0;
    color: #666;
    font-size: 0.9rem;
  }

  p {
    margin: 0;
    font-size: 1rem;
    color: ${props => props.status === 'good' ? '#28a745' : props.status === 'warning' ? '#ffc107' : '#dc3545'};
  }
`;

const TestButton = styled.button`
  padding: 0.5rem 1rem;
  background: var(--primary-color);
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  margin-right: 1rem;
  margin-bottom: 1rem;

  &:hover {
    background: #357abd;
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const LogList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 1rem 0;
`;

const LogItem = styled.li`
  margin: 0.5rem 0;
  padding: 0.75rem;
  background: #fff;
  border-left: 4px solid ${props => props.type === 'error' ? '#dc3545' : props.type === 'warning' ? '#ffc107' : '#28a745'};
  border-radius: 4px;
  font-size: 0.9rem;
`;

const TestSection = styled.div`
  margin-bottom: 1.5rem;

  h3 {
    margin: 0 0 1rem 0;
    color: var(--primary-color);
    font-size: 1.1rem;
  }
`;

const AnalyticsTest = () => {
  const [testResults, setTestResults] = useState({
    analytics: {
      pageViews: null,
      events: null,
      userProperties: null,
      customDimensions: null
    },
    performance: {
      pageLoad: null,
      networkRequests: null,
      rendering: null,
      traces: null
    },
    monitoring: {
      errorTracking: null,
      consoleLogging: null,
      customMetrics: null
    }
  });

  const [logs, setLogs] = useState([]);

  const addLog = (message, type = 'info') => {
    const timestamp = new Date().toISOString();
    setLogs(prev => [...prev, { timestamp, message, type }]);
  };

  const testGoogleAnalytics = async () => {
    try {
      const analytics = getAnalytics();
      
      // Test page view tracking
      logEvent(analytics, 'page_view', {
        page_title: document.title,
        page_location: window.location.href,
        page_path: window.location.pathname
      });

      // Test custom event tracking
      logEvent(analytics, 'test_event', {
        test_property: 'test_value',
        timestamp: Date.now()
      });

      // Test user properties
      setUserProperties(analytics, {
        user_type: 'tester',
        test_timestamp: Date.now()
      });

      setTestResults(prev => ({
        ...prev,
        analytics: {
          ...prev.analytics,
          pageViews: true,
          events: true,
          userProperties: true
        }
      }));

      addLog('Google Analytics tests completed successfully');
    } catch (error) {
      setTestResults(prev => ({
        ...prev,
        analytics: {
          ...prev.analytics,
          pageViews: false,
          events: false,
          userProperties: false
        }
      }));
      addLog(`Google Analytics error: ${error.message}`, 'error');
    }
  };

  const testPerformanceMonitoring = async () => {
    try {
      const perf = getPerformance();
      
      // Start a custom trace
      const testTrace = trace(perf, 'test_trace');
      testTrace.start();

      // Simulate some work
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Stop the trace
      testTrace.stop();

      // Get performance metrics
      const pageLoadTime = window.performance.timing.loadEventEnd - window.performance.timing.navigationStart;
      const resources = performance.getEntriesByType('resource');
      const renderTime = performance.getEntriesByType('paint');

      setTestResults(prev => ({
        ...prev,
        performance: {
          pageLoad: pageLoadTime < 3000,
          networkRequests: resources.length > 0,
          rendering: renderTime.length > 0,
          traces: true
        }
      }));

      addLog('Performance monitoring tests completed successfully');
    } catch (error) {
      setTestResults(prev => ({
        ...prev,
        performance: {
          pageLoad: false,
          networkRequests: false,
          rendering: false,
          traces: false
        }
      }));
      addLog(`Performance monitoring error: ${error.message}`, 'error');
    }
  };

  const testErrorMonitoring = async () => {
    try {
      // Test console logging
      console.log('Test log message');
      console.info('Test info message');
      console.warn('Test warning message');
      console.error('Test error message');

      // Test error tracking
      const oldOnError = window.onerror;
      window.onerror = (message, source, lineno, colno, error) => {
        addLog(`Error captured: ${message}`, 'error');
        if (oldOnError) oldOnError(message, source, lineno, colno, error);
        return true;
      };

      // Trigger a test error
      setTimeout(() => {
        try {
          throw new Error('Test error for monitoring');
        } catch (error) {
          addLog('Error monitoring captured test error successfully');
        }
      }, 100);

      setTestResults(prev => ({
        ...prev,
        monitoring: {
          errorTracking: true,
          consoleLogging: true,
          customMetrics: true
        }
      }));

      addLog('Error monitoring tests completed successfully');
    } catch (error) {
      setTestResults(prev => ({
        ...prev,
        monitoring: {
          errorTracking: false,
          consoleLogging: false,
          customMetrics: false
        }
      }));
      addLog(`Error monitoring test failed: ${error.message}`, 'error');
    }
  };

  const runAllTests = async () => {
    setLogs([]);
    await testGoogleAnalytics();
    await testPerformanceMonitoring();
    await testErrorMonitoring();
  };

  const getStatusFromValue = (category, metric, value) => {
    if (value === null) return 'error';
    return value ? 'good' : 'error';
  };

  return (
    <AnalyticsContainer>
      <TestSection>
        <h3>Analytics and Monitoring Tests</h3>
        <TestButton onClick={runAllTests}>
          Run All Analytics Tests
        </TestButton>

        <TestResults>
          <h3>Google Analytics</h3>
          <TestGrid>
            <TestCard status={getStatusFromValue('analytics', 'pageViews', testResults.analytics.pageViews)}>
              <h4>Page View Tracking</h4>
              <p>{testResults.analytics.pageViews ? 'Working' : 'Not working'}</p>
            </TestCard>
            <TestCard status={getStatusFromValue('analytics', 'events', testResults.analytics.events)}>
              <h4>Event Tracking</h4>
              <p>{testResults.analytics.events ? 'Working' : 'Not working'}</p>
            </TestCard>
            <TestCard status={getStatusFromValue('analytics', 'userProperties', testResults.analytics.userProperties)}>
              <h4>User Properties</h4>
              <p>{testResults.analytics.userProperties ? 'Working' : 'Not working'}</p>
            </TestCard>
          </TestGrid>

          <h3>Performance Monitoring</h3>
          <TestGrid>
            <TestCard status={getStatusFromValue('performance', 'pageLoad', testResults.performance.pageLoad)}>
              <h4>Page Load Time</h4>
              <p>{testResults.performance.pageLoad ? 'Acceptable' : 'Too slow'}</p>
            </TestCard>
            <TestCard status={getStatusFromValue('performance', 'networkRequests', testResults.performance.networkRequests)}>
              <h4>Network Requests</h4>
              <p>{testResults.performance.networkRequests ? 'Tracked' : 'Not tracked'}</p>
            </TestCard>
            <TestCard status={getStatusFromValue('performance', 'traces', testResults.performance.traces)}>
              <h4>Custom Traces</h4>
              <p>{testResults.performance.traces ? 'Working' : 'Not working'}</p>
            </TestCard>
          </TestGrid>

          <h3>Error Monitoring</h3>
          <TestGrid>
            <TestCard status={getStatusFromValue('monitoring', 'errorTracking', testResults.monitoring.errorTracking)}>
              <h4>Error Tracking</h4>
              <p>{testResults.monitoring.errorTracking ? 'Working' : 'Not working'}</p>
            </TestCard>
            <TestCard status={getStatusFromValue('monitoring', 'consoleLogging', testResults.monitoring.consoleLogging)}>
              <h4>Console Logging</h4>
              <p>{testResults.monitoring.consoleLogging ? 'Working' : 'Not working'}</p>
            </TestCard>
            <TestCard status={getStatusFromValue('monitoring', 'customMetrics', testResults.monitoring.customMetrics)}>
              <h4>Custom Metrics</h4>
              <p>{testResults.monitoring.customMetrics ? 'Working' : 'Not working'}</p>
            </TestCard>
          </TestGrid>

          {logs.length > 0 && (
            <>
              <h3>Test Logs</h3>
              <LogList>
                {logs.map((log, index) => (
                  <LogItem key={index} type={log.type}>
                    [{log.timestamp}] {log.message}
                  </LogItem>
                ))}
              </LogList>
            </>
          )}
        </TestResults>
      </TestSection>
    </AnalyticsContainer>
  );
};

export default AnalyticsTest; 